import { Tile, TileType, TraySlot, GameState, TRAY_SIZE } from './types';
import { generateLevel, LEVELS } from './levels';
import { getEndlessLevel } from './generator';

export function isOpen(tile: Tile, fieldTiles: Tile[]): boolean {
  return !fieldTiles.some(
    t => t.id !== tile.id &&
         t.layer === tile.layer + 1 &&
         Math.abs(t.row - tile.row) < 1 &&
         Math.abs(t.col - tile.col) < 1,
  );
}

// Add tile to tray and sort same types together.
// Returns new tray and the matched type (if 3 appear), or null.
export function addAndSort(
  tray: TraySlot[],
  tile: Tile,
): { tray: TraySlot[]; matchType: TileType | null } {
  const filled = tray.filter((s): s is Tile => s !== null);
  filled.push(tile);
  filled.sort((a, b) => a.type !== b.type ? a.type - b.type : a.id - b.id);

  const newTray: TraySlot[] = Array<TraySlot>(TRAY_SIZE).fill(null);
  filled.forEach((t, i) => { newTray[i] = t; });

  const matchType = findMatchType(newTray);
  return { tray: newTray, matchType };
}

function findMatchType(tray: TraySlot[]): TileType | null {
  const counts = new Map<TileType, number>();
  for (const s of tray) {
    if (s !== null) counts.set(s.type, (counts.get(s.type) ?? 0) + 1);
  }
  for (const [type, count] of counts) {
    if (count >= 3) return type;
  }
  return null;
}

// Returns which slot index in the (sorted) new tray the given tile occupies.
export function findTileSlot(tray: TraySlot[], tile: Tile): number {
  return tray.findIndex(s => s !== null && s.id === tile.id);
}

// Remove exactly 3 tiles of the given type and compact remaining to the left.
export function removeMatch(tray: TraySlot[], type: TileType): TraySlot[] {
  let removed = 0;
  const filtered = tray.filter(s => {
    if (s !== null && s.type === type && removed < 3) { removed++; return false; }
    return true;
  });
  const newTray: TraySlot[] = Array<TraySlot>(TRAY_SIZE).fill(null);
  filtered.forEach((s, i) => { newTray[i] = s; });
  return newTray;
}

export function isTrayFull(tray: TraySlot[]): boolean {
  return tray.every(s => s !== null);
}

export function isWon(state: GameState): boolean {
  return state.fieldTiles.length === 0 && state.tray.every(s => s === null);
}

// Find a hint: first open tile on field whose type also appears somewhere
// (on field or in tray), prioritising types already in tray.
export function findHint(state: GameState): Tile | null {
  const trayTypes = new Set(
    state.tray.filter((s): s is Tile => s !== null).map(t => t.type),
  );

  const openTiles = state.fieldTiles.filter(t => isOpen(t, state.fieldTiles));

  // Prefer tiles that match a type already in tray
  const priority = openTiles.find(t => trayTypes.has(t.type));
  if (priority) return priority;

  // Otherwise, any open tile whose type has another copy somewhere
  const fieldTypeCounts = new Map<TileType, number>();
  for (const t of state.fieldTiles) {
    fieldTypeCounts.set(t.type, (fieldTypeCounts.get(t.type) ?? 0) + 1);
  }
  return openTiles.find(t => (fieldTypeCounts.get(t.type) ?? 0) > 1) ?? null;
}

export function createState(levelIdx: number): GameState {
  // копия тайлов: кеш генератора не должен делить объекты с игровым стейтом
  const fieldTiles = levelIdx < LEVELS.length
    ? generateLevel(LEVELS[levelIdx])
    : getEndlessLevel(levelIdx).tiles.map(t => ({ ...t }));
  return {
    fieldTiles,
    tray: Array<TraySlot>(TRAY_SIZE).fill(null),
    pending: null,
    phase: 'playing',
    level: levelIdx,
    flyAnim: null,
    matchAnim: null,
    particles: [],
    hintTileId: null,
    hintStartMs: 0,
    undo: null,
  };
}
